// Vendors
import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TxButton } from 'texkit-ui';

// Hooks
import { useLanguageSelector } from '../../hooks/useLanguageSelector';

// Selectors
import { selectUtmParams } from '../../reducers/auth/authSelectors';

// Helpers
import { txtTermsUrl, texasGovHelpDeskUrl, texasGovHelpDeskUrlEs, stateOfTexasUrl } from '../../constants/appUrls';
import { buildUrl } from '../../lib/utmHelpers';

// Components
import Logo from '../Logo/Logo';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { Trans } from '../Trans';
import Icon from '../Icon/Icon';

// Styles
import '../../pages/auth-page.scss';

const AuthCardLayout = ({ children, className = '', showBookmarkWarning = false }) => {
  const { i18n } = useTranslation('auth');
  const { locale, isLanguageMenuOpen, languageWrapperRef, toggleLanguageMenu, handleLanguageSelect } = useLanguageSelector();
  const utmParams = useSelector(selectUtmParams);

  const txtSupportUrl = locale === 'es' ? texasGovHelpDeskUrlEs : texasGovHelpDeskUrl;
  const currentYear = new Date().getFullYear();
  const termsUrl = `${txtTermsUrl}?lang=${locale}`;

  const handleOpenTerms = () => {
    window.open(buildUrl(termsUrl, utmParams), '_blank', 'noopener,noreferrer');
  };

  const handleOpenSupport = () => {
    window.open(buildUrl(txtSupportUrl, utmParams), '_blank', 'noopener,noreferrer');
  };

  const handleOpenTexasGov = () => {
    window.open(stateOfTexasUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="sign-in-page">
      <header className="texas-gov-banner" role="banner">
        <div className="texas-gov-banner-content">
          <Trans
            file="auth"
            id="texasGovBanner"
            fallback="An official website of the Texas state government."
          />
        </div>
      </header>
      
      <div className={`sign-in-card ${className}`.trim()}>
        <div className="sign-in-card-header">
          <div className="sign-in-logo">
            <Logo className="sign-in-logo-mark" />
          </div>
          <LanguageSelector
            locale={locale}
            isLanguageMenuOpen={isLanguageMenuOpen}
            languageWrapperRef={languageWrapperRef}
            toggleLanguageMenu={toggleLanguageMenu}
            handleLanguageSelect={handleLanguageSelect}
          />
        </div>

        <div className="sign-in-card-body" role="main">{children}</div>

        <div className="sign-in-card-footer">
          <div className="sign-in-legal-links">
            <TxButton
              type="button"
              className="sign-in-link-button"
              onClick={handleOpenTerms}
            >
              <Trans file="auth" id="txtTermsText" />
            </TxButton>
            <TxButton
              type="button"
              className="sign-in-link-button"
              onClick={handleOpenSupport}
            >
              <Trans file="auth" id="txtSupportText" />
            </TxButton>
          </div>
        </div>
      </div>

      {showBookmarkWarning && (
        <div className="sign-in-bookmark-warning">
          <p className="sign-in-bookmark-warning-text">
            <strong><Trans file="auth" id="doNotBookmarkTitle" /></strong>{' '}
            <Trans file="auth" id="doNotBookmarkBody" />
          </p>
        </div>
      )}

      <footer className="sign-in-footer" role="contentinfo">
        <div className="sign-in-footer-content">
          <TxButton
            type="button"
            className="sign-in-footer-logo-link"
            onClick={handleOpenTexasGov}
            aria-label="State of Texas Logo"
          >
            <Icon 
              name="stateOfTexas" 
              className="sign-in-footer-logo"
              aria-label="State of Texas Logo"
            />
          </TxButton>
          <div className="sign-in-footer-copyright">
            {i18n.t('auth:copyright', { year: currentYear })}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthCardLayout;
